<template>
    <base-dialog :loading="loading" :value="dialog" :title="title" :width="440" @input="onInput" @close="hide">
        <div class="form-group">
            <span class="form-label">{{ $t('business.activity') }}</span>
            <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="activities" :value="activityId"
                item-text="name" item-value="id" no-object dense solo with-selected-dot
                @change="activityId = $event" />
        </div>

        <div class="form-group mt-4">
            <span class="form-label">{{ $t('business.day') }}</span>
            <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="dayOptions" :value="day"
                item-text="name" item-value="value" no-object dense solo @change="day = $event" />
        </div>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.start_time') }}</span>
                <kimsa-combobox :items="startOptions" :value="startTime" item-text="name" item-value="value" no-object
                    dense solo @change="onChangeStart" />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.end_time') }}</span>
                <kimsa-combobox :items="endOptions" :value="endTime" item-text="name" item-value="value" no-object dense
                    solo :readonly="endOptions.length < 2" @change="endTime = $event" />
            </div>
        </div>

        <div class="form-group mt-4">
            <span class="form-label">{{ $t('business.capacity') }}</span>
            <kimsa-text-field placeholder="15" :value="capacity" @change="capacity = $event" input-mode="numeric"
                dense force-value />
            <span v-if="taken > 0" class="shift-hint">
                {{ $t('business.capacity_taken_hint', { n: taken }) }}
            </span>
        </div>

        <span v-if="error" class="shift-error">{{ error }}</span>

        <template v-slot:footer>
            <dialog-actions :confirm-label="confirmLabel" :disabled="!valid" :loading="loading" @cancel="hide"
                @confirm="save">
                <template v-slot:extra>
                    <button v-if="isEdit" type="button" class="shift-delete" @click="remove">
                        {{ $t('common.delete') }}
                    </button>
                </template>
            </dialog-actions>
        </template>
        <kimsa-confirm ref="deleteConfirm" :title="$t('business.delete_shift')" :content="deleteContent"
            :persistent="false" alert-mode show-cancel @confirm="onConfirmDelete" />
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { capitalize } from "@/utils/Common";
import { DAY_END_HOUR, TIME_SLOTS, WEEK_DAYS, memberCount, parseAmount, toMinutes, toTime } from "@/utils/Business";

const DEFAULT_DURATION = 60

export default {
    name: "ShiftDialog",
    components: { BaseDialog, DialogActions, KimsaTextField, KimsaCombobox, KimsaConfirm },
    props: {
        activities: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        lastShift: {
            type: Object,
            default: null,
        },
        bookings: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            shift: null,
            activityId: null,
            day: 1,
            startTime: '08:00',
            endTime: '09:00',
            capacity: '',
        }
    },
    computed: {
        isEdit(vm = this) {
            return !!vm.shift?.id
        },
        title(vm = this) {
            return vm.isEdit ? vm.$t('business.edit_shift') : vm.$t('business.new_shift')
        },
        confirmLabel(vm = this) {
            return vm.isEdit ? vm.$t('common.save') : vm.$t('common.create')
        },
        startOptions(vm = this) {
            return vm.withCurrent(TIME_SLOTS.filter(time => toMinutes(time) < DAY_END_HOUR * 60), vm.startTime)
        },
        endOptions(vm = this) {
            return vm.withCurrent(TIME_SLOTS.filter(time => toMinutes(time) > toMinutes(vm.startTime)), vm.endTime)
        },
        dayOptions(vm = this) {
            void vm.$i18n.locale
            return WEEK_DAYS.map(value => ({
                value,
                name: capitalize(vm.$moment().isoWeekday(value).format('dddd')),
            }))
        },
        taken(vm = this) {
            return memberCount(vm.shift, vm.bookings)
        },
        deleteContent(vm = this) {
            if (vm.taken > 0) return vm.$tc('business.delete_shift_body_members', vm.taken, { n: vm.taken })
            return vm.$t('business.delete_shift_body')
        },
        error(vm = this) {
            if (vm.endTime && vm.startTime && vm.endTime <= vm.startTime) {
                return vm.$t('business.end_before_start')
            }
            const capacity = parseAmount(vm.capacity)
            if (capacity > 0 && capacity < vm.taken) {
                return vm.$t('business.capacity_below_members', { n: vm.taken })
            }
            return ''
        },
        valid(vm = this) {
            return !!vm.activityId && !!vm.day && !vm.error && parseAmount(vm.capacity) > 0
        },
    },
    methods: {
        show(shift = null, preset = {}) {
            const vm = this
            const last = shift ? null : vm.lastShift
            vm.shift = shift
            vm.activityId = shift?.activityId || preset.activityId || last?.activityId || vm.activities[0]?.id || null
            vm.day = shift?.day || preset.day || last?.day || 1
            vm.startTime = shift?.startTime || preset.startTime || last?.startTime || '08:00'
            vm.endTime = shift?.endTime || preset.endTime || vm.endFor(vm.startTime, vm.durationOf(last))
            vm.capacity = shift?.capacity || last?.capacity || ''
            vm.dialog = true
        },
        withCurrent(times, current) {
            const values = current && !times.includes(current) ? [...times, current].sort() : times
            return values.map(value => ({ value, name: value }))
        },
        durationOf(shift) {
            if (!shift?.startTime || !shift?.endTime) return DEFAULT_DURATION
            const duration = toMinutes(shift.endTime) - toMinutes(shift.startTime)
            return duration > 0 ? duration : DEFAULT_DURATION
        },
        endFor(startTime, duration = DEFAULT_DURATION) {
            return toTime(Math.min(toMinutes(startTime) + duration, DAY_END_HOUR * 60))
        },
        onChangeStart(value) {
            const vm = this
            const duration = vm.durationOf({ startTime: vm.startTime, endTime: vm.endTime })
            vm.startTime = value
            vm.endTime = vm.endFor(value, duration)
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        save() {
            const vm = this
            if (!vm.valid) return
            vm.$emit('save', {
                id: vm.shift?.id || null,
                activityId: vm.activityId,
                day: vm.day,
                startTime: vm.startTime,
                endTime: vm.endTime,
                capacity: parseAmount(vm.capacity),
            })
            vm.hide()
        },
        remove() {
            this.$refs.deleteConfirm.show()
        },
        onConfirmDelete() {
            const vm = this
            vm.$emit('delete', vm.shift)
            vm.hide()
        },
    },
}
</script>

<style scoped>
.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.shift-error {
    display: block;
    margin-top: 14px;
    font-size: 11px;
    color: var(--v-error-base);
}

.shift-hint {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.shift-delete {
    padding: 0 20px;
    height: 52px;
    border-right: 1px solid var(--v-outline-base);
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--v-error-base);
}
</style>
